import { useCallback, useEffect, useRef } from "react";
import Environment from "../../utils/Environment";

const PRICE_TOKENS = new Set(["GEMS", "ETH", "USDT", "USDC"]);
/** After an input-triggered fetch, ignore further keystrokes for this long. */
const INPUT_FETCH_COOLDOWN_MS = 5000;

/**
 * Fetches USD price for the active payment token only.
 * - On wallet connect / payment token change (immediate)
 * - On pay-amount input: fetch when user types; then 5s before listening again
 * - Reuses in-flight request for the same token (no parallel duplicates)
 */
export default function usePaymentPriceRefresh({
  mainTag,
  account,
  payInputRaw,
  ethPrice,
  getTokenPriceUsd,
  refetchGemsPrice,
  setEthPrice,
  setUsdtPriceUsd,
  setUsdcPriceUsd,
}) {
  const inFlightRef = useRef(null);
  const inputListeningRef = useRef(true);
  const inputCooldownTimerRef = useRef(null);

  const refreshPriceForToken = useCallback(
    async (token) => {
      switch (token) {
        case "GEMS":
          return refetchGemsPrice();
        case "ETH": {
          const ethPx = await ethPrice();
          if (ethPx) setEthPrice(ethPx);
          return ethPx;
        }
        case "USDT": {
          const px = await getTokenPriceUsd(Environment.busd);
          if (px > 0) setUsdtPriceUsd(px);
          return px;
        }
        case "USDC": {
          const px = await getTokenPriceUsd(Environment.usdc);
          if (px > 0) setUsdcPriceUsd(px);
          return px;
        }
        default:
          return 0;
      }
    },
    [refetchGemsPrice, ethPrice, getTokenPriceUsd, setEthPrice, setUsdtPriceUsd, setUsdcPriceUsd],
  );

  const refreshActivePaymentPrice = useCallback(
    async ({ token } = {}) => {
      const tag = token ?? mainTag;
      if (!PRICE_TOKENS.has(tag)) return 0;

      const existing = inFlightRef.current;
      if (existing?.tag === tag) return existing.promise;

      const promise = refreshPriceForToken(tag).finally(() => {
        if (inFlightRef.current?.tag === tag) inFlightRef.current = null;
      });
      inFlightRef.current = { tag, promise };
      return promise;
    },
    [mainTag, refreshPriceForToken],
  );

  const startInputFetchCooldown = useCallback(() => {
    clearTimeout(inputCooldownTimerRef.current);
    inputCooldownTimerRef.current = setTimeout(() => {
      inputListeningRef.current = true;
    }, INPUT_FETCH_COOLDOWN_MS);
  }, []);

  useEffect(() => {
    inputListeningRef.current = true;
    clearTimeout(inputCooldownTimerRef.current);
    return () => clearTimeout(inputCooldownTimerRef.current);
  }, [mainTag]);

  useEffect(() => {
    if (!account || !PRICE_TOKENS.has(mainTag)) return undefined;
    refreshActivePaymentPrice({ token: mainTag });
    return undefined;
  }, [account, mainTag, refreshActivePaymentPrice]);

  useEffect(() => {
    if (!account || !PRICE_TOKENS.has(mainTag)) return undefined;
    if (payInputRaw === "" || payInputRaw == null) return undefined;
    const n = parseFloat(payInputRaw);
    if (!Number.isFinite(n) || n <= 0) return undefined;

    if (!inputListeningRef.current) return undefined;

    inputListeningRef.current = false;
    refreshActivePaymentPrice({ token: mainTag }).finally(() => {
      startInputFetchCooldown();
    });

    return undefined;
  }, [account, mainTag, payInputRaw, refreshActivePaymentPrice, startInputFetchCooldown]);

  useEffect(
    () => () => clearTimeout(inputCooldownTimerRef.current),
    [],
  );

  return { refreshActivePaymentPrice };
};
